import { z } from 'zod';
import { accessGate, cents, configKey, editableText, entityId, isoDateTime, publishable, slug } from './primitives.js';

/**
 * MODELO COMERCIAL (Cap. 16, 17, 22)
 *
 * Planos, limites, produtos avulsos, cupons e promocoes sao dados. Gratuito,
 * Bronze, Prata, Ouro e Diamante nao existem como constantes no codigo — sao
 * cinco linhas em `plans`. Criar um sexto plano e trabalho de administrador.
 */

/**
 * Limites de um plano. Toda checagem de limite no sistema le esta tabela;
 * nenhum numero magico em regra de negocio (Cap. 16.6).
 */
export const planLimits = z.object({
  /** -1 = ilimitado, em todos os campos abaixo. */
  maxActiveListings: z.number().int().min(-1).default(1),
  maxPhotosPerListing: z.number().int().min(-1).default(1),
  maxVideosPerListing: z.number().int().min(-1).default(0),
  maxDocumentsPerListing: z.number().int().min(-1).default(0),
  /** Validade do anuncio em dias (Cap. 15.1). */
  listingDurationDays: z.number().int().min(1).default(30),
  /** Renovacoes automaticas permitidas antes de exigir acao humana. */
  autoRenewals: z.number().int().min(-1).default(0),
  maxFeaturedListings: z.number().int().min(-1).default(0),
  maxSubUsers: z.number().int().min(-1).default(0),
  maxSavedSearches: z.number().int().min(-1).default(3),
  /** Posicao base na ordenacao. Maior = aparece antes entre iguais. */
  rankingBoost: z.number().int().min(0).default(0),
  /** Habilita a pagina de empreendimento da incorporadora (Cap. 3.7). */
  developmentPages: z.boolean().default(false),
  /** Recebe leads direto ou passa pela retaguarda DIREX (Cap. 19.1). */
  contactRouting: z.enum(['direct', 'centralized', 'inherit']).default('inherit'),
  /** Relatorios liberados por chave. */
  reports: z.array(configKey).default([]),
});

export type PlanLimits = z.infer<typeof planLimits>;

export const billingPeriod = z.enum(['monthly', 'quarterly', 'semiannual', 'annual', 'one_time', 'free']);

export const planPrice = z.object({
  period: billingPeriod,
  amount: cents,
  currency: z.string().length(3).default('BRL'),
  /** Preco riscado, apenas visual. */
  compareAtAmount: cents.nullable().default(null),
  /** Dias de teste antes da primeira cobranca (Cap. 2.1: periodos gratuitos). */
  trialDays: z.number().int().min(0).default(0),
  /** Codigo do preco no gateway. Preenchido pelo servico de pagamentos. */
  gatewayPriceId: z.string().nullable().default(null),
});

export type PlanPrice = z.infer<typeof planPrice>;

export const plan = z.object({
  id: entityId,
  slug,
  name: editableText,
  description: editableText.nullable().default(null),
  /** Publico-alvo declarado — usado para filtrar a vitrine de planos. */
  audiences: z.array(z.enum(['owner', 'broker', 'agency', 'developer', 'company', 'organization', 'other'])).default([]),
  prices: z.array(planPrice).default([]),
  limits: planLimits.default({}),
  /** Bullets exibidos no card. Texto livre editavel. */
  benefits: z.array(editableText).default([]),
  /** Destaca visualmente o plano na vitrine. */
  highlighted: z.boolean().default(false),
  badge: editableText.nullable().default(null),
  publication: publishable.default({}),
});

export type Plan = z.infer<typeof plan>;

/** Produtos avulsos do Cap. 17.1 — destaque, topo da busca, impulsionamento. */
export const addOnProduct = z.object({
  id: entityId,
  slug,
  name: editableText,
  description: editableText.nullable().default(null),
  kind: z.enum([
    'listing_highlight',
    'search_top',
    'extra_photos',
    'video',
    'boost',
    'banner',
    'professional_page',
    'listing_pack',
    'sponsored_campaign',
    'service',
  ]),
  price: cents,
  currency: z.string().length(3).default('BRL'),
  /** Duracao do efeito em dias; 0 = permanente/consumivel. */
  durationDays: z.number().int().min(0).default(0),
  /** Quantidade concedida — ex.: 10 fotos extras, 5 anuncios. */
  quantity: z.number().int().min(0).default(1),
  gate: accessGate.default({ roles: [], plans: [] }),
  publication: publishable.default({}),
});

export type AddOnProduct = z.infer<typeof addOnProduct>;

export const discountKind = z.enum(['percentage', 'fixed_amount', 'free_period']);

export const coupon = z.object({
  id: entityId,
  code: z.string().min(3).max(32).toUpperCase(),
  kind: discountKind,
  /** percentage: 0-100 | fixed_amount: centavos | free_period: dias. */
  value: z.number().min(0),
  /** Restringe a planos/produtos especificos. Vazio = vale para todos. */
  appliesToPlans: z.array(slug).default([]),
  appliesToProducts: z.array(slug).default([]),
  maxRedemptions: z.number().int().min(-1).default(-1),
  maxRedemptionsPerUser: z.number().int().min(1).default(1),
  redemptions: z.number().int().min(0).default(0),
  minimumAmount: cents.nullable().default(null),
  /** Primeira compra apenas. */
  firstPurchaseOnly: z.boolean().default(false),
  publication: publishable.default({}),
});

export type Coupon = z.infer<typeof coupon>;

/** Campanha promocional com fluxo Criar -> Editar -> Publicar -> Pausar -> Encerrar (Cap. 22.3). */
export const promotion = z.object({
  id: entityId,
  name: editableText,
  status: z.enum(['draft', 'scheduled', 'running', 'paused', 'ended']).default('draft'),
  couponId: entityId.nullable().default(null),
  /** Banner/aviso exibido enquanto a promocao roda. */
  announcement: editableText.nullable().default(null),
  startsAt: isoDateTime.nullable().default(null),
  endsAt: isoDateTime.nullable().default(null),
});

export type Promotion = z.infer<typeof promotion>;

export const commerceConfig = z.object({
  version: z.string(),
  plans: z.array(plan).default([]),
  addOns: z.array(addOnProduct).default([]),
  coupons: z.array(coupon).default([]),
  promotions: z.array(promotion).default([]),
  /** Slug do plano concedido a quem se cadastra sem escolher nada. */
  defaultPlanSlug: slug.default('gratuito'),
  /** Metodos habilitados na vitrine (Cap. 18.1). */
  paymentMethods: z.array(z.enum(['pix', 'credit_card', 'boleto'])).default(['pix', 'credit_card']),
});

export type CommerceConfig = z.infer<typeof commerceConfig>;
